import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Standard "title on the left, action on the right" header used inside
 * dashboard cards and section panels. Use this everywhere so fonts,
 * spacing, and wrap behavior stay identical across the app.
 *
 * Usage:
 *   <SectionHeader
 *     title="Invoicing - AR"
 *     description="Customer invoices and payments."
 *     action={<SectionLink to="/invoicing/invoices">View invoices</SectionLink>}
 *   />
 */
export function SectionHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'mb-3 flex flex-wrap items-start justify-between gap-3',
        className,
      )}
    >
      <div className="min-w-0">
        <h4 className="text-base font-semibold text-foreground sm:text-lg">
          {title}
        </h4>
        {description ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
